import { useTranslation } from 'locales';
import { useEffect } from 'react';
import { Route, Switch, useHistory, useLocation } from 'react-router-dom';
import Modals from 'shared/components/Modals';
import { useAuthenticate } from 'shared/hooks/use-authenticate';
import { getEnvIcon } from 'shared/utils/environment';
import { PrivateRoutes } from './Private';
import { OnboardingWrapper } from './Private/OnboardingWrapper';
import { PrivateLoader } from './Private/PrivateLoader';
import { PrivateRoutesWrapper } from './Private/PrivateRoutesWrapper';
import { DebtorReactionRoute } from './Public/DebtorReaction/DebtorReactionRoute';
import { Login } from './Public/Login';
import { Logout } from './Public/Logout';
import { useTenant } from './TenantValidator';

const AUTH_ROUTES: ReadonlyArray<string> = ['/login', '/logout'];

//! Do not call companyConfiguration in this component, it will cause an error
//! if the onboarding has not been completed yet
export const App = () => {
  useTranslation(); // Removing this call causes the translation system to error out. Whatever the reason is, it's wrong.
  useSetPageTitle();
  const history = useHistory();
  const location = useLocation();
  const { canProceedIntoAuthenticatedDomain, isAuth0Loading, isAuthenticated } = useAuthenticate();

  useEffect(() => {
    //* The debtor reaction portal is a public access page that should have been another app
    if (location.pathname.includes('debtor-reaction')) return;
    //* This does not cause an infinite loop because /login immediately performs a redirect to auth0
    if (!isAuth0Loading && !isAuthenticated) {
      const { pathname, search } = history.location;
      const isAuthRoute = AUTH_ROUTES.includes(pathname);
      history.push('/login', isAuthRoute ? undefined : { returnTo: `${pathname}${search}` });
    }
  }, [isAuth0Loading, isAuthenticated, history]);

  return (
    <>
      <Modals />
      <Switch>
        <Route exact path="/logout" component={Logout} />
        <Route exact path="/login" component={Login} />
        {/* I belong in a separate application */}
        <Route exact path="/invoices/:id/debtor-reaction" component={DebtorReactionRoute} />

        {canProceedIntoAuthenticatedDomain && (
          <PrivateLoader>
            <Switch>
              <Route path="/onboarding" component={OnboardingWrapper} />
              <Route path="/">
                <PrivateRoutesWrapper>
                  <PrivateRoutes />
                </PrivateRoutesWrapper>
              </Route>
            </Switch>
          </PrivateLoader>
        )}
      </Switch>
    </>
  );
};

function useSetPageTitle() {
  const tenant = useTenant();
  useEffect(() => {
    document.title = `${getEnvIcon()}${tenant?.companyName ?? 'Recovr'} `;
  }, [tenant?.companyName]);
}
